export interface ValidationResult {
  valid: boolean;
  message: string;
}

/**
 * Validate email address
 */
export function validateEmail(email: string): ValidationResult {
  if (!email) {
    return { valid: false, message: 'Email is required' };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { valid: false, message: 'Please enter a valid email address' };
  }

  return { valid: true, message: '' };
}

/**
 * Validate phone number
 */
export function validatePhone(phone: string): ValidationResult {
  if (!phone) {
    return { valid: false, message: 'Phone number is required' };
  }

  // Remove common formatting characters
  const cleaned = phone.replace(/[\s\-\(\)]/g, '');
  
  // Check if it's all digits and has reasonable length
  if (!/^\d+$/.test(cleaned)) {
    return { valid: false, message: 'Phone number must contain only digits' };
  }

  if (cleaned.length < 7 || cleaned.length > 15) {
    return { valid: false, message: 'Phone number must be between 7 and 15 digits' };
  }

  return { valid: true, message: '' };
}

/**
 * Validate required field
 */
export function validateRequired(value: any): ValidationResult {
  if (value === null || value === undefined || value === '') {
    return { valid: false, message: 'This field is required' };
  }

  if (typeof value === 'string' && value.trim().length === 0) {
    return { valid: false, message: 'This field is required' };
  }

  return { valid: true, message: '' };
}

/**
 * Validate number
 */
export function validateNumber(value: any, min?: number, max?: number): ValidationResult {
  if (value === null || value === undefined || value === '') {
    return { valid: false, message: 'This field is required' };
  }

  const num = typeof value === 'number' ? value : parseFloat(String(value));

  if (isNaN(num)) {
    return { valid: false, message: 'Please enter a valid number' };
  }

  if (min !== undefined && num < min) {
    return { valid: false, message: `Value must be at least ${min}` };
  }

  if (max !== undefined && num > max) {
    return { valid: false, message: `Value must be at most ${max}` };
  }

  return { valid: true, message: '' };
}

/**
 * Validate date
 */
export function validateDate(date: string | Date | null | undefined): ValidationResult {
  if (!date) {
    return { valid: false, message: 'Date is required' };
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return { valid: false, message: 'Please enter a valid date' };
  }

  return { valid: true, message: '' };
}

/**
 * Validate URL
 */
export function validateUrl(url: string): ValidationResult {
  if (!url) {
    return { valid: false, message: 'URL is required' };
  }

  try {
    // Try to create a URL object
    new URL(url);
    return { valid: true, message: '' };
  } catch {
    // If URL constructor fails, try with http:// prefix
    try {
      new URL(`http://${url}`);
      return { valid: true, message: '' };
    } catch {
      return { valid: false, message: 'Please enter a valid URL' };
    }
  }
}

/**
 * Validate string length
 */
export function validateLength(value: string, min?: number, max?: number): ValidationResult {
  if (!value) {
    return { valid: false, message: 'This field is required' };
  }

  const length = value.length;

  if (min !== undefined && length < min) {
    return { valid: false, message: `Must be at least ${min} characters` };
  }

  if (max !== undefined && length > max) {
    return { valid: false, message: `Must be at most ${max} characters` };
  }

  return { valid: true, message: '' };
}

/**
 * Validate pattern (regex)
 */
export function validatePattern(value: string, pattern: RegExp, message?: string): ValidationResult {
  if (!value) {
    return { valid: false, message: 'This field is required' };
  }

  if (!pattern.test(value)) {
    return { valid: false, message: message || 'Invalid format' };
  }

  return { valid: true, message: '' };
}

/**
 * Generic validator that applies multiple validation rules
 */
export function validateField(
  value: any,
  rules: {
    required?: boolean;
    type?: 'email' | 'phone' | 'number' | 'date' | 'url';
    min?: number;
    max?: number;
    pattern?: RegExp;
    patternMessage?: string;
    custom?: (value: any) => ValidationResult;
  }
): ValidationResult {
  // Check required first
  if (rules.required) {
    const requiredResult = validateRequired(value);
    if (!requiredResult.valid) {
      return requiredResult;
    }
  } else if (!value) {
    // If not required and no value, it's valid
    return { valid: true, message: '' };
  }

  // Type validation
  if (rules.type) {
    let typeResult: ValidationResult;
    switch (rules.type) {
      case 'email':
        typeResult = validateEmail(value);
        break;
      case 'phone':
        typeResult = validatePhone(value);
        break;
      case 'number':
        typeResult = validateNumber(value, rules.min, rules.max);
        break;
      case 'date':
        typeResult = validateDate(value);
        break;
      case 'url':
        typeResult = validateUrl(value);
        break;
      default:
        typeResult = { valid: true, message: '' };
    }
    if (!typeResult.valid) {
      return typeResult;
    }
  }

  // Length validation for strings
  if (typeof value === 'string' && (rules.min !== undefined || rules.max !== undefined)) {
    const lengthResult = validateLength(value, rules.min, rules.max);
    if (!lengthResult.valid) {
      return lengthResult;
    }
  }

  // Pattern validation
  if (rules.pattern) {
    const patternResult = validatePattern(value, rules.pattern, rules.patternMessage);
    if (!patternResult.valid) {
      return patternResult;
    }
  }

  // Custom validation
  if (rules.custom) {
    const customResult = rules.custom(value);
    if (!customResult.valid) {
      return customResult;
    }
  }

  return { valid: true, message: '' };
}
